import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Clock, Target, Zap, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DemoForm from "@/components/forms/DemoForm";

const benefits = [
  {
    icon: TrendingUp,
    title: "Maximiza Ingresos",
    description: "Captura el valor óptimo de cada habitación ajustando precios según la demanda real del mercado.",
  },
  {
    icon: Clock,
    title: "Respuesta en Tiempo Real",
    description: "Reacciona instantáneamente a cambios en la competencia, eventos locales y patrones de demanda.",
  },
  {
    icon: Target,
    title: "Precios Precisos",
    description: "Elimina la adivinanza con precios calculados científicamente basados en más de 50 variables.",
  },
  {
    icon: Zap,
    title: "Automatización Total",
    description: "Libera a tu equipo de ajustes manuales mientras el sistema optimiza 24/7.",
  },
];

const faqs = [
  {
    question: "¿Qué es el pricing dinámico en hotelería?",
    answer: "El pricing dinámico es una estrategia que ajusta automáticamente las tarifas de habitaciones en tiempo real basándose en factores como demanda, ocupación, competencia, eventos locales, día de la semana y temporada. Permite maximizar ingresos vendiendo a precios óptimos en cada momento."
  },
  {
    question: "¿Con qué frecuencia cambian los precios?",
    answer: "La frecuencia de cambios depende de tu estrategia. Zimoo puede actualizar precios desde cada hora hasta varias veces al día, siempre respetando los límites mínimos y máximos que definas para mantener consistencia de marca."
  },
  {
    question: "¿No confundirá esto a mis huéspedes?",
    answer: "No. El pricing dinámico es una práctica estándar en la industria que los consumidores ya esperan. Es similar a como funcionan las aerolíneas. Lo importante es mantener transparencia y no realizar cambios erráticos."
  },
  {
    question: "¿Puedo mantener control sobre los precios?",
    answer: "Absolutamente. Tú defines pisos y techos de tarifas, reglas de negocio y puedes aprobar o rechazar recomendaciones. Zimoo optimiza dentro de los parámetros que tú estableces."
  },
];

export default function PricingDinamicoPage() {
  useEffect(() => {
    document.title = "Pricing Dinámico para Hoteles | Tarifas Optimizadas en Tiempo Real - Zimoo";
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-7xl mx-auto container-padding relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="label-text mb-4 block">PRICING DINÁMICO</span>
            <h1 className="heading-1 mb-6">
              Tarifas Optimizadas en{" "}
              <span className="text-gradient">Tiempo Real</span>
            </h1>
            <p className="body-text text-lg mb-8">
              Deja que la inteligencia artificial calcule el precio perfecto para cada habitación, cada día, cada momento.
            </p>
            <a href="#demo-pricing">
              <Button className="btn-primary">
                Contáctanos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* AEO Block */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="text-xl font-semibold text-white mb-3">¿Qué es el pricing dinámico hotelero?</h2>
          <p className="text-gray-300 leading-relaxed">
            El pricing dinámico hotelero es una estrategia de fijación de precios que ajusta las tarifas de habitaciones automáticamente en respuesta a cambios en la demanda del mercado, competencia, eventos y otros factores. Permite a los hoteles maximizar ingresos vendiendo al precio óptimo en cada momento.
          </p>
        </div>
      </section>

      {/* How it Works Visual */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">CÓMO FUNCIONA</span>
            <h2 className="heading-2 mb-4">
              Pricing dinámico impulsado por IA
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                {[
                  { title: "Análisis de Demanda", desc: "El sistema analiza búsquedas, reservas y tendencias para entender la demanda real." },
                  { title: "Monitoreo de Competencia", desc: "Rastrea precios de competidores en tiempo real para mantener competitividad." },
                  { title: "Detección de Eventos", desc: "Identifica eventos locales, festividades y factores que afectan la demanda." },
                  { title: "Cálculo del Precio Óptimo", desc: "Algoritmos de IA calculan el precio que maximiza ingresos para cada escenario." },
                  { title: "Actualización Automática", desc: "Los precios se actualizan en todos tus canales de distribución instantáneamente." },
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#1ABCB4] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A0A0A] mb-1">{item.title}</h3>
                      <p className="text-[#404040] text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-2xl p-8 border border-black/5">
              <h3 className="font-semibold text-[#0A0A0A] mb-6">Ejemplo de Pricing Dinámico</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-black/5">
                  <div>
                    <p className="text-sm text-[#404040]">Lunes (baja demanda)</p>
                    <p className="font-semibold text-[#0A0A0A]">Habitación Estándar</p>
                  </div>
                  <p className="text-xl font-bold text-[#0A0A0A]">$89 USD</p>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-black/5">
                  <div>
                    <p className="text-sm text-[#404040]">Viernes (alta demanda)</p>
                    <p className="font-semibold text-[#0A0A0A]">Habitación Estándar</p>
                  </div>
                  <p className="text-xl font-bold text-[#0A0A0A]">$149 USD</p>
                </div>
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-[#1ABCB4] to-[#5200FF] rounded-xl text-white">
                  <div>
                    <p className="text-sm opacity-80">Evento especial</p>
                    <p className="font-semibold">Habitación Estándar</p>
                  </div>
                  <p className="text-xl font-bold">$199 USD</p>
                </div>
              </div>
              <p className="text-sm text-[#404040] mt-4 text-center">
                El mismo tipo de habitación, diferentes precios según demanda
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">BENEFICIOS</span>
            <h2 className="heading-2 mb-4">
              Resultados que puedes medir
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="card-hover p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1ABCB4] to-[#5200FF] flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#0A0A0A] mb-2">{benefit.title}</h3>
                <p className="text-[#404040] text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {[
              { value: "+15-25%", label: "Incremento en RevPAR" },
              { value: "24/7", label: "Optimización continua" },
              { value: "<1 min", label: "Tiempo de actualización" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-gradient mb-2">{stat.value}</p>
                <p className="text-[#404040]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-3 mb-6">Factores que Considera el Pricing Dinámico</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {[
              "Demanda histórica y actual",
              "Precios de competidores",
              "Eventos y festividades locales",
              "Día de la semana y temporada",
              "Anticipación de la reserva",
              "Ocupación actual del hotel",
              "Tendencias de búsqueda online",
              "Condiciones climáticas",
              "Tipo de habitación",
              "Canal de distribución",
              "Segmento de cliente",
              "Promociones activas"
            ].map((factor, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-[#1ABCB4]" />
                <span className="text-[#404040]">{factor}</span>
              </div>
            ))}
          </div>

          <h2 className="heading-3 mb-6">¿Por qué Implementar Pricing Dinámico?</h2>
          <p className="body-text mb-4">
            En un mercado cada vez más competitivo, los hoteles que mantienen tarifas estáticas pierden ingresos significativos. El pricing dinámico te permite:
          </p>
          <ul className="space-y-3 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <span className="text-[#404040]">Capturar el valor máximo durante periodos de alta demanda</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <span className="text-[#404040]">Estimular ocupación con precios competitivos en temporada baja</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <span className="text-[#404040]">Responder rápidamente a cambios del mercado y competencia</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <span className="text-[#404040]">Tomar decisiones basadas en datos, no en intuición</span>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Preguntas Frecuentes</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-[#0A0A0A] font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#404040]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section id="demo-pricing" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">EMPIEZA AHORA</span>
              <h2 className="heading-2 mb-6">
                Implementa Pricing Dinámico en tu Hotel
              </h2>
              <p className="body-text mb-6">
                Solicita una demo y descubre cómo el pricing dinámico puede transformar los ingresos de tu hotel.
              </p>
              <Link to="/revenue-management-hoteles" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Aprende más sobre Revenue Management <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <DemoForm sourcePage="pricing-dinamico" />
          </div>
        </div>
      </section>
    </div>
  );
}
